import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import axios from 'axios';
import config from '../../config';

import '../../styles/global.css';
import '../../styles/buttons.css';
import '../../styles/popup.css';
import '../../styles/filters.css';
import '../../styles/pagination.css';

import DeleteConfirmation from '../general/deleteConfirmation';
import DetailedAccountPanel from '../people/detailedAccount';
import PageTable from '../general/pageTable';
import Filter from '../general/filters';
import Loading from '../general/loading';

const PeopleTable = ({ rolesToShow, showFilters, showRoleFilter = true }) => {
  const queryClient = useQueryClient();

  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const [selectedPerson, setSelectedPerson] = useState(null);
  const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);
  const [personToDelete, setPersonToDelete] = useState(null);

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['v2-people', page, selectedRole, selectedProperty],
    queryFn: () => fetchPeopleData(page, rowsPerPage, selectedRole, selectedProperty, rolesToShow),
    keepPreviousData: true,
  });

  const peopleData = data?.results || [];
  const totalPages = data?.totalPages || 1;

  const handleDelete = async (person) => {
    if (!person) return;
    try {
      await axios.delete(`${config.API_URL}/v2/user/${person.account.user._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      toast.success('User deleted successfully!');
      queryClient.invalidateQueries(['v2-people']);
      setDeleteOverlayOpen(false);
      setPersonToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user.');
    }
  };

  const roleOptions = Array.from(
    new Set(peopleData.map((p) => p.role).filter((r) => rolesToShow.includes(r)))
  ).map((r) => r.charAt(0).toUpperCase() + r.slice(1));

  const propertyOptions = Array.from(
    new Set(peopleData.map((p) => p.property?.name).filter(Boolean))
  );

  const filteredRows = peopleData.filter(
    (p) =>
      rolesToShow.includes(p.role) &&
      (!selectedProperty || p.property?.name === selectedProperty)
  );
  const total = totalPages || 1;
  const maxVisible = 10;
  const startPage = Math.floor((page - 1) / maxVisible) * maxVisible + 1;
  const endPage = Math.min(startPage + maxVisible - 1, total);

  return (
    <div className="full-page" style={{ minHeight: '77vh' }}>
      <div className="page-content">
        <div className="property-table-container table-wrapper">
          {isLoading && <Loading />}
          {isError && <p className="error-message">Failed to load people.</p>}

          {!isLoading && !isError && (
            <>
              {showFilters && (
                <div className="filter-container">
                  <Filter
                    placeholder="Property"
                    options={propertyOptions}
                    value={selectedProperty}
                    onChange={(val) => {
                      setSelectedProperty(val);
                      setPage(1);
                    }}
                  />
                  {showRoleFilter && (
                    <Filter
                      placeholder="Role"
                      options={roleOptions}
                      value={selectedRole}
                      onChange={(val) => {
                        setSelectedRole(val);
                        setPage(1);
                      }}
                    />
                  )}
                </div>
              )}

              <PageTable
                rows={filteredRows}
                columns={[
                  { label: 'Role', accessor: (row) => row.role.charAt(0).toUpperCase() + row.role.slice(1), width: '10%' },
                  { label: 'User', accessor: (row) => row.account.user.givenName + ' ' + row.account.user.lastName, width: '12%' },
                  { label: 'Email Address', accessor: 'account.user.email', width: '12%' },
                  { label: 'Property Name', accessor: 'property.name', width: '15%' },
                  {
                    label: 'Address',
                    accessor: (row) =>
                      `${row.property.address.streetNumber}, ${row.property.address.street}, ${row.property.address.city}, ${row.property.address.state}, ${row.property.address.country} - ${row.property.address.postalCode}`,
                    width: '45%',
                  },
                ]}
                hasButtons
                hasEdit={false}
                hasDelete
                onDelete={(row) => {
                  setPersonToDelete(row);
                  setDeleteOverlayOpen(true);
                }}
                onRowClick={(row) => setSelectedPerson(row)}
              />
            </>
          )}

          {deleteOverlayOpen && (
            <DeleteConfirmation
              setIsOverlayOpen={setDeleteOverlayOpen}
              onConfirm={() => handleDelete(personToDelete)}
            />
          )}

          {selectedPerson && (
            <DetailedAccountPanel
              selectedPerson={selectedPerson}
              account={selectedPerson.account}
              property={selectedPerson.property}
              propertyRoleId={selectedPerson._id}
              role={selectedPerson.role}
              details={selectedPerson.details}
              onClose={() => setSelectedPerson(null)}
            />
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination-controls">
            <div className="pagination-buttons">
              <button
                className="pagination-button"
                onClick={() => setPage((p) => (Math.max(p.page - 1, 1)))}
                disabled={page === 1}
              >
                <i className="fas fa-chevron-left"></i>
              </button>

              {startPage > 1 && (
                <button
                  className="pagination-page"
                  onClick={() => setPage(startPage - 1)}
                >
                  ...
                </button>
              )}

              {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                const num = startPage + i;
                return (
                  <button
                    key={num}
                    className={`pagination-page ${page === num ? 'active' : ''}`}
                    onClick={() => setPage(num)}
                  >
                    {num}
                  </button>
                );
              })}

              {endPage < total && (
                <button className="pagination-page" onClick={() => setPage(endPage + 1)}
                >
                  ...
                </button>
              )}

              <button
                className="pagination-button"
                onClick={() => setPage((p) => (Math.min(p.page + 1, total)))}
                disabled={page === total}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const fetchPeopleData = async (page, limit, selectedRole, selectedProperty, rolesToShow) => {
  try {
    const params = { page, limit, role: selectedRole ? selectedRole.toLowerCase() : rolesToShow.join(','), sortBy: 'createdAt', sortOrder: 'desc', };
    if (selectedProperty) params.property = selectedProperty;

    const response = await axios.get(`${config.API_URL}/v2/role/propertyRole/query`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      params,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching paginated people:', error);
    return { results: [], totalPages: 1, totalResults: 0 };
  }
};

export default PeopleTable;
